"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Users, DollarSign,
  Zap,
  Stethoscope, Calendar, ClipboardList, Clock,
  Settings, BookOpen, Stethoscope as StethoscopeMd
} from "lucide-react";
import { AuthProvider, useAuth } from "./auth-context";
import { FiltrosClinicaProvider } from "../contexts/FiltrosClinicaContext";

// === MENÚ CLÍNICA MULTIESPECIALIDAD (REFATORIZADO Jun 5 2026) ===
const CLINICA_MENU = [
  { name: "Mi Agenda",        path: "/medico/hoy",             icon: StethoscopeMd},
  { name: "Calendario",     path: "/medico/calendario",     icon: Calendar       },
  { name: "Mi Grilla",       path: "/medico/mi-grilla",      icon: Clock          },
  { name: "Calendario",     path: "/turnos/calendario",    icon: Calendar       },
  { name: "Pacientes",      path: "/pacientes",            icon: Users          },
  { name: "Turnos",         path: "/turnos",               icon: ClipboardList  },
] as const;

// Soporte para subgrupos en Configuración
type ConfigItem =
  | { name: string; path: string; icon: React.ElementType }
  | { name: string; children: { name: string; path: string; icon: React.ElementType }[] };

const CONFIG_MENU: ConfigItem[] = [
  {
    name: "Clínica",
    icon: Settings,
    children: [
      { name: "Especialidades", path: "/configuracion/especialidades", icon: Stethoscope },
      { name: "Profesionales",  path: "/configuracion/profesionales",  icon: Users        },
    ],
  },
  { name: "Agenda",         path: "/configuracion/agenda",   icon: Calendar    },
  { name: "Obras Sociales", path: "/obras-sociales",         icon: DollarSign  },
  { name: "Nomenclador",    path: "/nomencladores",          icon: BookOpen    },
];

function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, loading, esMedico } = useAuth();
  const isDemo = user?.rol === "demo_viewer";

  if (pathname === "/login") {
    return <>{children}</>;
  }

  // Redirect to login if not authenticated
  if (!loading && !user) {
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    return null;
  }

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#08090a", color: "#f7f8f8" }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, minWidth: 220,
        background: "#0f1011",
        borderRight: "1px solid rgba(255,255,255,0.06)",
        display: "flex", flexDirection: "column",
        position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 50, overflowY: "auto",
      }}>
        <div style={{ padding: "24px 20px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: "linear-gradient(135deg, #5e6ad2, #7170ff)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Zap size={16} color="white" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#f7f8f8" }}>
                {user?.nombre || "Usuario"}
              </div>
              <div style={{ fontSize: 11, color: "#62666d", marginTop: 2 }}>
                {user?.empresa?.nombre || "Empresa"}
                {isDemo && (
                  <span style={{
                    marginLeft: 6, padding: "1px 6px", borderRadius: 4,
                    background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)",
                    color: "#f59e0b", fontSize: 9, fontWeight: 700, letterSpacing: "0.05em",
                    verticalAlign: "middle", textTransform: "uppercase",
                  }}>
                    DEMO
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main nav — condicional por rol */}
        <nav style={{ padding: "12px 10px", display: "flex", flexDirection: "column" }}>
          {CLINICA_MENU.map((item) => {
            // Ocultar "Mi Agenda", "Calendario médico" y "Mi Grilla" para NO médicos
            if (!esMedico && (item.path === "/medico/hoy" || item.path === "/medico/calendario" || item.path === "/medico/mi-grilla")) return null;
            // Ocultar "Calendario" (turnos) y "Turnos" para médicos (ellos usan Mi Agenda + Calendario)
            if (esMedico && (item.path === "/turnos" || item.path === "/turnos/calendario")) return null;

            const Icon = item.icon;
            const active = pathname === item.path;
            return (
              <Link key={item.path} href={item.path} style={{ textDecoration: "none" }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", borderRadius: 6, marginBottom: 1,
                  background: active ? "rgba(94,106,210,0.15)" : "transparent",
                  color: active ? "#7170ff" : "#8a8f98",
                  fontSize: 13, fontWeight: active ? 500 : 400,
                  cursor: "pointer",
                  border: active ? "1px solid rgba(113,112,255,0.2)" : "1px solid transparent",
                }}>
                  <Icon size={15} />
                  {item.name}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Config section — SOLO admin/recepcionista (médico no gestiona) */}
        {!esMedico && (<>
          <div style={{ padding: "0 10px 8px" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#c9cdd4",
            letterSpacing: "0.06em", textTransform: "uppercase", padding: "8px 10px 6px",
            borderTop: "2px solid rgba(94,106,210,0.3)", marginTop: 8,
          }}>
            Configuración
          </div>
          {CONFIG_MENU.map((item) => {
            // Subgrupo con children
            if ("children" in item) {
              return (
                <div key={item.name} style={{ marginBottom: 6 }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "5px 10px", borderRadius: 6,
                    color: "#c9cdd4", fontSize: 12, fontWeight: 600,
                    cursor: "default",
                  }}>
                    {item.name}
                  </div>
                  {item.children.map((child) => {
                    const ChildIcon = child.icon;
                    const active = pathname === child.path;
                    return (
                      <Link key={child.path} href={child.path} style={{ textDecoration: "none" }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "6px 10px 6px 32px", borderRadius: 6, marginBottom: 1,
                          background: active ? "rgba(94,106,210,0.10)" : "transparent",
                          color: active ? "#7170ff" : "#62666d",
                          fontSize: 12, fontWeight: active ? 500 : 400,
                          cursor: "pointer",
                          border: active ? "1px solid rgba(113,112,255,0.15)" : "1px solid transparent",
                        }}>
                          <ChildIcon size={13} />
                          {child.name}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              );
            }
            // Item normal
            const nItem = item as { name: string; path: string; icon: React.ElementType };
            const Icon = nItem.icon;
            const active = pathname === nItem.path;
            return (
              <Link key={nItem.path} href={nItem.path} style={{ textDecoration: "none" }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 10px", borderRadius: 6, marginBottom: 1,
                  background: active ? "rgba(94,106,210,0.10)" : "transparent",
                  color: active ? "#7170ff" : "#62666d",
                  fontSize: 12, fontWeight: active ? 500 : 400,
                  cursor: "pointer",
                  border: active ? "1px solid rgba(113,112,255,0.15)" : "1px solid transparent",
                }}>
                  <Icon size={14} />
                  {item.name}
                </div>
              </Link>
            );
          })}
        </div>
        </>)}

        <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 11, color: "#62666d" }}>
          <button onClick={() => {
            if (typeof window !== "undefined") {
              localStorage.clear();
              window.location.href = "/login";
            }
          }} style={{ background: "none", border: "none", color: "#62666d", cursor: "pointer", fontSize: 11 }}>
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={{ flex: 1, marginLeft: 220, minHeight: "100vh", background: "#08090a" }}>
        {isDemo && (
          <div style={{
            background: "rgba(245,158,11,0.1)", borderBottom: "1px solid rgba(245,158,11,0.2)",
            padding: "8px 20px", display: "flex", alignItems: "center", gap: 8,
            fontSize: 12, color: "#f59e0b", fontWeight: 500,
          }}>
            <span style={{
              background: "rgba(245,158,11,0.2)", border: "1px solid rgba(245,158,11,0.3)",
              borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
            }}>MODO DEMO</span>
            <span>Solo lectura — Los cambios no se guardarán. Contacto: wa.me/5493425291558</span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

export default function ShellProvider({ children }: { children: React.ReactNode }) {
  return (
    <AuthProvider>
      <FiltrosClinicaProvider>
        <AppShell>{children}</AppShell>
      </FiltrosClinicaProvider>
    </AuthProvider>
  );
}
